import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

interface RankingPbRow {
  id: string;
  placement: number;
  athlete_name: string;
  box_name: string | null;
  category: string;
  hibrido_games_points: number;
  sun_games_points: number;
  first_places: number;
  total_accumulated: number;
}

export interface RankingAthleteEntry {
  id: string;
  athlete_name: string;
  box_name: string | null;
  team_name: string | null;
  category: string;
  event_name: string;
  team_placement: number;
  score_per_event: number;
  hibrido_games_points: number;
  sun_games_points: number;
  hibrido_got_points: number;
  total_accumulated: number;
  first_places: number;
  overall_placement: number;
}

// Tabela oficial: 100, 96, 92, 88... (-4 por colocação)
export function getPointsFromPlacement(placement: number): number {
  return Math.max(0, 100 - (placement - 1) * 4);
}

// Load ranking from ranking_pb table (Supabase)
async function loadRankingFromDatabase(): Promise<RankingAthleteEntry[]> {
  const { data, error } = await (supabase as any)
    .from("ranking_pb")
    .select("*");

  if (error) {
    console.error("[useHibridoGamesRanking] Error loading ranking_pb:", error);
    throw error;
  }

  return ((data || []) as RankingPbRow[]).map((row) => ({
    id: row.id,
    athlete_name: row.athlete_name,
    box_name: row.box_name,
    team_name: null,
    category: row.category,
    event_name: "Ranking Paraíba (Oficial)",
    team_placement: 0,
    score_per_event: 0,
    hibrido_games_points: row.hibrido_games_points,
    sun_games_points: row.sun_games_points,
    hibrido_got_points: 0,
    total_accumulated: row.total_accumulated,
    first_places: row.first_places,
    overall_placement: row.placement,
  }));
}

// Data de desbloqueio do Ranking Paraíba (após o Híbrido Games 11-12 abril)
const RANKING_UNLOCK_DATE = new Date("2026-04-13T00:00:00-03:00");
export const isRankingLocked = () => new Date() < RANKING_UNLOCK_DATE;

export function useHibridoGamesRanking(category?: string) {
  const queryClient = useQueryClient();
  const locked = isRankingLocked();

  // Real-time: auto-refresh when ranking_pb changes
  useEffect(() => {
    if (locked) return;

    const channel = supabase
      .channel("ranking-paraiba-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ranking_pb" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["ranking-paraiba"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, locked]);

  return useQuery({
    queryKey: ["ranking-paraiba", category, locked],
    queryFn: async () => {
      const all = await loadRankingFromDatabase();

      // Durante o Híbrido Games: mostra atletas, mas zera a pontuação
      const results = locked
        ? all.map((a) => ({
            ...a,
            score_per_event: 0,
            hibrido_games_points: 0,
            sun_games_points: 0,
            hibrido_got_points: 0,
            total_accumulated: 0,
            team_placement: 0,
            first_places: 0,
            overall_placement: 0,
          }))
        : all;

      if (!category || category === "Todas") return results;
      return results.filter((a) => a.category === category);
    },
    staleTime: locked ? 120_000 : 30_000,
    refetchInterval: locked ? false : 60_000,
  });
}

export function useHibridoGamesCategories() {
  return useQuery({
    queryKey: ["ranking-paraiba-categories"],
    queryFn: async () => {
      const all = await loadRankingFromDatabase();
      const cats = Array.from(new Set(all.map((a) => a.category)));

      const normalizeCategory = (cat: string) =>
        cat
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim();

      const getOrder = (cat: string) => {
        const c = normalizeCategory(cat);
        if (c.includes("rx") && c.includes("masculino")) return 10;
        if (c.includes("rx") && c.includes("feminino")) return 11;
        if (c.includes("intermediario") && c.includes("masculino")) return 20;
        if (c.includes("intermediario") && c.includes("feminino")) return 21;
        if (c.includes("scaled") && c.includes("masculino")) return 30;
        if (c.includes("scaled") && c.includes("feminino")) return 31;

        if (c.includes("rx")) return 15;
        if (c.includes("intermediario")) return 25;
        if (c.includes("scaled")) return 35;
        return 100;
      };

      cats.sort((a, b) => getOrder(a) - getOrder(b));
      return cats.length > 0 ? [...cats, "Todas"] : ["RX Masculino", "Todas"];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useHibridoGamesEvents() {
  return useQuery({
    queryKey: ["hibrido-games-events"],
    queryFn: async () => [] as { id: string; name: string; slug: string }[],
  });
}
